import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CategoryOutlined,
  CheckCircleOutline,
  DragIndicator,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocationOnOutlined,
  MonetizationOnOutlined,
  PeopleOutline,
  PersonSearchOutlined,
  WorkOutline,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from "@mui/icons-material";
import {
  DEFAULT_WIDGET_ORDER,
  useWidgetPreferences,
} from "../Context/WidgetPreferencesContext";
import { useSnackbar } from "../Context/SnackbarProvider";
import { PageContainer, TealSwitch } from "../Styles/AppStyles";
import { COLORS } from "../Styles/Colors";

type WidgetMeta = {
  label: string;
  metric: "Cost" | "Volume";
  group: string;
  icon: React.ReactNode;
};

const widgetMeta: Record<string, WidgetMeta> = {
  productCategory: {
    label: "Usage by Product Category",
    metric: "Cost",
    group: "Product Category",
    icon: <CategoryOutlined fontSize="small" />,
  },
  productCategoryVolume: {
    label: "Usage by Product Category",
    metric: "Volume",
    group: "Product Category",
    icon: <CategoryOutlined fontSize="small" />,
  },
  user: {
    label: "Usage by Employee",
    metric: "Cost",
    group: "Employee",
    icon: <PeopleOutline fontSize="small" />,
  },
  employeeVolume: {
    label: "Usage by Employee",
    metric: "Volume",
    group: "Employee",
    icon: <PeopleOutline fontSize="small" />,
  },
  requesterVolume: {
    label: "Usage by Requester",
    metric: "Volume",
    group: "Requester",
    icon: <PersonSearchOutlined fontSize="small" />,
  },
  requesterCost: {
    label: "Usage by Requester",
    metric: "Cost",
    group: "Requester",
    icon: <PersonSearchOutlined fontSize="small" />,
  },
  location: {
    label: "Usage by Location",
    metric: "Cost",
    group: "Location",
    icon: <LocationOnOutlined fontSize="small" />,
  },
  locationVolume: {
    label: "Usage by Location",
    metric: "Volume",
    group: "Location",
    icon: <LocationOnOutlined fontSize="small" />,
  },
  poJobCost: {
    label: "Usage by PO / Job No.",
    metric: "Cost",
    group: "PO / Job",
    icon: <WorkOutline fontSize="small" />,
  },
};

export const WidgetsPage = () => {
  const {
    widgetOrder,
    visibleWidgets,
    updateWidgetOrder,
    setWidgetVisible,
    moveWidget,
    lastSavedAt,
    ready,
  } = useWidgetPreferences();
  const { showSnackbar } = useSnackbar();

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [showSavedBadge, setShowSavedBadge] = useState(false);
  const lastAnnouncedSaveRef = useRef(lastSavedAt);

  useEffect(() => {
    if (!lastSavedAt || lastSavedAt === lastAnnouncedSaveRef.current) return;
    lastAnnouncedSaveRef.current = lastSavedAt;
    setShowSavedBadge(true);
    showSnackbar("Widget layout saved", "success");
    const timer = window.setTimeout(() => setShowSavedBadge(false), 2500);
    return () => window.clearTimeout(timer);
  }, [lastSavedAt, showSnackbar]);

  if (!ready) {
    return (
      <PageContainer>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 4,
              mx: 3,
              maxWidth: 860,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h5" sx={{ mb: 2 }}>
              Loading...
            </Typography>
          </Paper>
        </Box>
      </PageContainer>
    );
  }

  const handleToggle = (widgetId: string) => {
    const isVisible = visibleWidgets.includes(widgetId);
    if (isVisible && visibleWidgets.length === 1) return;
    setWidgetVisible(widgetId, !isVisible);
  };

  const handleDragStart = (e: React.DragEvent, widgetId: string) => {
    setDraggedId(widgetId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", widgetId);
  };

  const handleDragOver = (e: React.DragEvent, widgetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== widgetId) setDragOverId(widgetId);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = draggedId || e.dataTransfer.getData("text/plain");
    setDraggedId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const next = [...widgetOrder];
    const from = next.indexOf(sourceId);
    const to = next.indexOf(targetId);
    if (from === -1 || to === -1) return;

    next.splice(from, 1);
    next.splice(to, 0, sourceId);
    updateWidgetOrder(next);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const orderedWidgets = widgetOrder.filter(
    (id) =>
      (DEFAULT_WIDGET_ORDER as readonly string[]).includes(id) ||
      Boolean(widgetMeta[id]),
  );

  const visibleCount = orderedWidgets.filter((id) =>
    visibleWidgets.includes(id),
  ).length;

  return (
    <PageContainer>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          pb: 4,
        }}
      >
        <Box sx={{ mt: 4, mb: 3, maxWidth: 860 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1 }}
          >
            <Typography variant="h5">Customize Dashboard Widgets</Typography>
            {showSavedBadge && (
              <Chip
                size="small"
                icon={<CheckCircleOutline sx={{ fontSize: "16px !important" }} />}
                label="Layout saved"
                sx={{
                  fontWeight: 600,
                  bgcolor: `${COLORS.teal}18`,
                  color: COLORS.teal,
                  "& .MuiChip-icon": { color: COLORS.teal },
                }}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Toggle widgets on or off, and drag rows (or use the arrows) to set their
            order on the homepage. Changes are saved automatically and kept when you
            leave this page.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3, maxWidth: 860 }}
        >
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${COLORS.teal}0A 0%, #fff 60%)`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: `${COLORS.teal}18`,
                  color: COLORS.teal,
                }}
              >
                <VisibilityOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Visible on homepage
                </Typography>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {visibleCount} of {orderedWidgets.length}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 2.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${COLORS.orange}0A 0%, #fff 60%)`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: `${COLORS.orange}18`,
                  color: COLORS.orange,
                }}
              >
                <DragIndicator fontSize="small" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Reorder tip
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25, color: "text.primary" }}>
                  Drag rows or use the arrows to change layout order
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            maxWidth: 860,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#fafbfc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="text.primary">
              Available widgets
            </Typography>
            <Typography variant="caption" color="text.secondary">
              At least one must remain visible
            </Typography>
          </Box>

          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {orderedWidgets.map((widgetId, index) => {
              const isChecked = visibleWidgets.includes(widgetId);
              const isLastChecked = isChecked && visibleWidgets.length === 1;
              const meta = widgetMeta[widgetId];
              const label = meta?.label || widgetId;
              const isDragging = draggedId === widgetId;
              const isDropTarget = dragOverId === widgetId && draggedId !== widgetId;

              return (
                <Box
                  component="li"
                  key={widgetId}
                  draggable
                  onDragStart={(e) => handleDragStart(e, widgetId)}
                  onDragOver={(e) => handleDragOver(e, widgetId)}
                  onDrop={(e) => handleDrop(e, widgetId)}
                  onDragEnd={handleDragEnd}
                  onDragLeave={() => {
                    if (dragOverId === widgetId) setDragOverId(null);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.75,
                    borderBottom:
                      index < orderedWidgets.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    cursor: "grab",
                    position: "relative",
                    opacity: isDragging ? 0.45 : 1,
                    bgcolor: isDropTarget
                      ? `${COLORS.teal}0F`
                      : isChecked
                        ? "#fff"
                        : "#fafafa",
                    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                    boxShadow: isDropTarget
                      ? `inset 0 0 0 2px ${COLORS.teal}`
                      : "none",
                    "&:hover": {
                      bgcolor: isDropTarget ? `${COLORS.teal}0F` : `${COLORS.teal}06`,
                    },
                    "&:active": {
                      cursor: "grabbing",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: COLORS.lightGray,
                      flexShrink: 0,
                    }}
                  >
                    <DragIndicator sx={{ fontSize: 20 }} />
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      width: 22,
                      flexShrink: 0,
                      color: "text.disabled",
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {index + 1}
                  </Typography>

                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: isChecked ? `${COLORS.teal}14` : "action.hover",
                      color: isChecked ? COLORS.teal : "text.secondary",
                    }}
                  >
                    {meta?.icon || <MonetizationOnOutlined fontSize="small" />}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{
                          color: isChecked ? "text.primary" : "text.secondary",
                        }}
                      >
                        {label}
                      </Typography>
                      {meta && (
                        <Chip
                          size="small"
                          label={meta.metric}
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            bgcolor:
                              meta.metric === "Cost"
                                ? `${COLORS.orange}18`
                                : `${COLORS.teal}18`,
                            color:
                              meta.metric === "Cost" ? COLORS.orange : COLORS.teal,
                            border: "none",
                          }}
                        />
                      )}
                      {!isChecked && (
                        <Chip
                          size="small"
                          icon={<VisibilityOffOutlined sx={{ fontSize: "14px !important" }} />}
                          label="Hidden"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: "action.hover",
                            color: "text.secondary",
                            "& .MuiChip-icon": { color: "text.secondary" },
                          }}
                        />
                      )}
                    </Stack>
                    {isLastChecked && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          display: "block",
                          color: COLORS.orange,
                          fontWeight: 600,
                        }}
                      >
                        Required — keep at least one widget visible
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                    <Tooltip title="Move up" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Move up"
                          disabled={index === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            moveWidget(widgetId, "up");
                          }}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            width: 32,
                            height: 32,
                          }}
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Move down"
                          disabled={index === orderedWidgets.length - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            moveWidget(widgetId, "down");
                          }}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            width: 32,
                            height: 32,
                          }}
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip
                      title={
                        isLastChecked
                          ? "At least one widget must stay visible"
                          : isChecked
                            ? "Hide from homepage"
                            : "Show on homepage"
                      }
                      placement="top"
                    >
                      <span>
                        <TealSwitch
                          checked={isChecked}
                          disabled={isLastChecked}
                          onChange={() => handleToggle(widgetId)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          inputProps={{ "aria-label": `Toggle ${label}` }}
                        />
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </Box>
    </PageContainer>
  );
};
